import { useApolloClient } from "@apollo/client";
import { LabeledValue } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  TextField as MuiTextField,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Formik } from "formik";
import { isEqual } from "lodash-es";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AddressInput as AddressInputGQLType,
  PhoneType,
} from "../../../../clients/graphqlTypes.js";
import { AddressField } from "../../addresses/AddressField.js";
import { AddressWithBreaks } from "../../addresses/AddressWithBreaks.js";
import { getAddressInputFromAddress } from "../../addresses/getAddressInputFromAddress.js";
import { getAddressLabel, isAddressEqual } from "../../addresses/helpers.js";
import { CategoriesInput } from "../../categories/CategoriesInput.js";
import {
  CategoriesChips,
  CategoriesLabeledValue,
} from "../../categories/CategoriesLabeledValue.js";
import { PhonesField } from "../../phones/PhonesField.js";
import { usePhoneTypes } from "../../phones/usePhoneTypes.js";
import { SectionCard } from "../sections/SectionCard.js";
import {
  OrganisationWebsitesDetailsBox_OrganisationProfileFragment,
  OrganisationWebsitesDetailsBox_ViewerOrganisationFragment,
  useOrganisationWebsitesDetailsBox_ModifyProfileMutation,
} from "./OrganisationWebsitesDetailsBox.generated.js";

interface Props {
  organisation: OrganisationWebsitesDetailsBox_ViewerOrganisationFragment;
  profile: OrganisationWebsitesDetailsBox_OrganisationProfileFragment;
}

type AddressSource = "branch" | "billing" | "other";
type SimpleSource = "org" | "other";
type EditingField = "address" | "email" | "website" | "phones" | null;

function detectAddressSource(
  profileAddr: AddressInputGQLType | null | undefined,
  org: OrganisationWebsitesDetailsBox_ViewerOrganisationFragment
): AddressSource {
  if (!profileAddr) return "other";
  if (org.branchAddress && isAddressEqual(profileAddr, org.branchAddress))
    return "branch";
  if (org.billingAddress && isAddressEqual(profileAddr, org.billingAddress))
    return "billing";
  return "other";
}

// Syncs Formik address value into parent state so the Confirm button reacts to changes
function AddressFormSync({
  contactAddress,
  onSync,
}: {
  contactAddress: AddressInputGQLType | null | undefined;
  onSync: (contactAddress: AddressInputGQLType | null) => void;
}) {
  useEffect(() => {
    onSync(contactAddress ?? null);
  }, [contactAddress, onSync]);
  return null;
}

function ReadonlyFieldRow({
  label,
  value,
  onEdit,
  extraSpacing,
}: {
  label: string;
  value: React.ReactNode;
  onEdit: () => void;
  extraSpacing?: string;
}) {
  const { t } = useTranslate("Global");
  return (
    <LabeledValue label={label} notSet={!value}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={extraSpacing || 0}
      >
        <Typography variant="body2">{value || t("Not set")}</Typography>
        <IconButton color="primary" size="small" onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Stack>
    </LabeledValue>
  );
}

function EditableFieldRow({
  label,
  heading,
  saving,
  confirmDisabled,
  onCancel,
  onConfirm,
  children,
  extraSpacing,
}: {
  label?: string;
  heading?: React.ReactNode;
  saving: boolean;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  children: React.ReactNode;
  extraSpacing?: string;
}) {
  const { t } = useTranslate("Global");
  return (
    <Stack>
      {label && (
        <Typography variant="caption" color="secondary">
          {label}
        </Typography>
      )}
      {heading}
      <Stack spacing={0.5} pt={extraSpacing || 0}>
        {children}

        <Stack direction="row" justifyContent="flex-end" spacing={1} pt={0.5}>
          <Button
            size="small"
            variant="text"
            disabled={saving}
            onClick={onCancel}
          >
            {t("Cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={saving || !!confirmDisabled}
            onClick={onConfirm}
          >
            {t("Save")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

const formControlOverrideSx = {
  span: {
    fontSize: "0.875rem",
  },
};

type PhonesFormValues = {
  phones: Array<{ number: string; type: PhoneType; main: boolean }>;
};

type OrgPhoneToggle = {
  id: string;
  number: string;
  type: PhoneType;
  main: boolean;
  enabled: boolean;
};

function ContactSection({ organisation, profile }: Props) {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const [editingField, setEditingField] = useState<EditingField>(null);
  const [saving, setSaving] = useState(false);

  const [modify] = useOrganisationWebsitesDetailsBox_ModifyProfileMutation({
    client,
  });

  const [addrSource, setAddrSource] = useState<AddressSource>("other");
  const customAddressRef = useRef<AddressInputGQLType | null>(null);
  // Tracks the live Formik address value so the Confirm button reacts to changes
  const [addrFormValue, setAddrFormValue] =
    useState<AddressInputGQLType | null>(null);

  function openAddressEdit() {
    const source = detectAddressSource(profile.contactAddress, organisation);
    const initial =
      profile.contactAddress && source === "other"
        ? getAddressInputFromAddress(profile.contactAddress)
        : null;
    customAddressRef.current = initial;
    setAddrSource(source);
    setAddrFormValue(initial);
    setEditingField("address");
  }

  async function saveAddress() {
    setSaving(true);
    try {
      let contactAddress: AddressInputGQLType | null | undefined = undefined;
      if (addrSource === "branch" && organisation.branchAddress) {
        contactAddress = getAddressInputFromAddress(organisation.branchAddress);
      } else if (addrSource === "billing" && organisation.billingAddress) {
        contactAddress = getAddressInputFromAddress(
          organisation.billingAddress
        );
      } else {
        contactAddress = addrFormValue ?? null;
      }
      await modify({
        variables: { input: { slug: profile.slug, contactAddress } },
      });
      setEditingField(null);
    } catch (err) {
      if (err instanceof Error)
        enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  const [emailSource, setEmailSource] = useState<SimpleSource>("org");
  const [customEmail, setCustomEmail] = useState("");

  function openEmailEdit() {
    const isOrgEmail = profile.contactEmail === organisation.email;
    setEmailSource(isOrgEmail ? "org" : "other");
    setCustomEmail(isOrgEmail ? "" : (profile.contactEmail ?? ""));
    setEditingField("email");
  }

  async function saveEmail() {
    setSaving(true);
    try {
      const contactEmail =
        emailSource === "org"
          ? (organisation.email ?? null)
          : customEmail || null;
      await modify({
        variables: { input: { slug: profile.slug, contactEmail } },
      });
      setEditingField(null);
    } catch (err) {
      if (err instanceof Error)
        enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  const [websiteSource, setWebsiteSource] = useState<SimpleSource>("org");
  const [customWebsite, setCustomWebsite] = useState("");

  function openWebsiteEdit() {
    const isOrgWebsite =
      !!organisation.website && profile.contactWebsite === organisation.website;
    setWebsiteSource(isOrgWebsite ? "org" : "other");
    setCustomWebsite(isOrgWebsite ? "" : (profile.contactWebsite ?? ""));
    setEditingField("website");
  }

  async function saveWebsite() {
    setSaving(true);
    try {
      const contactWebsite =
        websiteSource === "org"
          ? (organisation.website ?? null)
          : customWebsite || null;
      await modify({
        variables: { input: { slug: profile.slug, contactWebsite } },
      });
      setEditingField(null);
    } catch (err) {
      if (err instanceof Error)
        enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  const [orgToggles, setOrgToggles] = useState<OrgPhoneToggle[]>([]);
  const [customPhonesInitial, setCustomPhonesInitial] =
    useState<PhonesFormValues>({ phones: [] });
  const customPhonesRef = useRef<PhonesFormValues>({ phones: [] });
  const { phoneTypeLabels } = usePhoneTypes();

  function openPhonesEdit() {
    const orgPhones = organisation.phones ?? [];
    const contactPhones = profile.contactPhones ?? [];
    setOrgToggles(
      orgPhones.map(op => ({
        id: op.id,
        number: op.number,
        type: op.type,
        main: op.main,
        enabled: contactPhones.some(cp => cp.number === op.number),
      }))
    );
    const custom = contactPhones.filter(
      cp => !orgPhones.some(op => op.number === cp.number)
    );
    setCustomPhonesInitial({
      phones: custom.map(cp => ({
        number: cp.number,
        type: cp.type,
        main: cp.main,
      })),
    });
    setEditingField("phones");
  }

  async function savePhones() {
    setSaving(true);
    try {
      const fromToggles = orgToggles
        .filter(p => p.enabled)
        .map(p => ({ number: p.number, type: p.type, main: p.main }));
      const fromCustom = customPhonesRef.current.phones
        .filter(p => p.number.trim())
        .map(p => ({ number: p.number, type: p.type, main: p.main }));
      await modify({
        variables: {
          input: {
            slug: profile.slug,
            contactPhones: [...fromToggles, ...fromCustom],
          },
        },
      });
      setEditingField(null);
    } catch (err) {
      if (err instanceof Error)
        enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  const addressDisplayValue = useMemo(
    () =>
      profile.contactAddress ? (
        <AddressWithBreaks
          address={profile.contactAddress}
          useCountry={false}
        />
      ) : null,
    [profile.contactAddress]
  );

  const phonesDisplayValue = useMemo(
    () =>
      profile.contactPhones?.length ? (
        <Stack spacing={0}>
          {profile.contactPhones.map((p, i) => (
            <Stack direction="row" spacing={1}>
              {p.type && (
                <Typography variant="body2" sx={{ width: 56 }}>
                  {phoneTypeLabels[p.type]}
                </Typography>
              )}
              <Typography variant="body2">{p.number}</Typography>
            </Stack>
          ))}
        </Stack>
      ) : null,
    [profile.contactPhones, phoneTypeLabels]
  );

  return (
    <Stack spacing={1}>
      <Typography pb={"6px"}>
        {t("Contact details", { ns: "OrganisationProfile" })}
      </Typography>

      {editingField !== "address" ? (
        <ReadonlyFieldRow
          label={t("Address", { ns: "OrganisationProfile" })}
          value={addressDisplayValue}
          onEdit={openAddressEdit}
          extraSpacing="3px"
        />
      ) : (
        <EditableFieldRow
          label={t("Address", { ns: "OrganisationProfile" })}
          saving={saving}
          confirmDisabled={
            addrSource === "other" && !addrFormValue?.streetLines1
          }
          onCancel={() => setEditingField(null)}
          onConfirm={saveAddress}
        >
          <RadioGroup
            value={addrSource}
            onChange={e => setAddrSource(e.target.value as AddressSource)}
          >
            {organisation.branchAddress && (
              <FormControlLabel
                value="branch"
                control={<Radio size="small" disabled={saving} />}
                label={`${t("Branch address", { ns: "OrganisationProfile" })} — ${getAddressLabel(organisation.branchAddress)}`}
                sx={formControlOverrideSx}
              />
            )}
            {organisation.billingAddress && (
              <FormControlLabel
                value="billing"
                control={<Radio size="small" disabled={saving} />}
                label={`${t("Billing address", { ns: "OrganisationProfile" })} — ${getAddressLabel(organisation.billingAddress)}`}
                sx={formControlOverrideSx}
              />
            )}
            <FormControlLabel
              value="other"
              control={<Radio size="small" disabled={saving} />}
              label={t("Other", { ns: "Global" })}
              sx={formControlOverrideSx}
            />
          </RadioGroup>
          {addrSource === "other" && (
            <Formik
              initialValues={{ contactAddress: customAddressRef.current }}
              onSubmit={() => {}}
              enableReinitialize={false}
            >
              {({ values }) => (
                <>
                  <AddressFormSync
                    contactAddress={values.contactAddress}
                    onSync={setAddrFormValue}
                  />
                  <AddressField
                    name="contactAddress"
                    label={t("Address", { ns: "OrganisationProfile" })}
                    disabled={saving}
                    showDetails={false}
                  />
                </>
              )}
            </Formik>
          )}
        </EditableFieldRow>
      )}

      {editingField !== "website" ? (
        <ReadonlyFieldRow
          label={t("Website", { ns: "OrganisationProfile" })}
          value={profile.contactWebsite}
          onEdit={openWebsiteEdit}
        />
      ) : (
        <EditableFieldRow
          label={t("Website", { ns: "OrganisationProfile" })}
          saving={saving}
          confirmDisabled={websiteSource === "other" && !customWebsite.trim()}
          onCancel={() => setEditingField(null)}
          onConfirm={saveWebsite}
        >
          <RadioGroup
            value={websiteSource}
            onChange={e => setWebsiteSource(e.target.value as SimpleSource)}
          >
            {organisation.website && (
              <FormControlLabel
                value="org"
                control={<Radio size="small" disabled={saving} />}
                label={organisation.website}
                sx={formControlOverrideSx}
              />
            )}
            <FormControlLabel
              value="other"
              control={<Radio size="small" disabled={saving} />}
              label={t("Other", { ns: "Global" })}
              sx={formControlOverrideSx}
            />
          </RadioGroup>
          {websiteSource === "other" && (
            <MuiTextField
              size="small"
              label={t("Website", { ns: "OrganisationProfile" })}
              value={customWebsite}
              onChange={e => setCustomWebsite(e.target.value)}
              disabled={saving}
              fullWidth
            />
          )}
        </EditableFieldRow>
      )}

      {editingField !== "email" ? (
        <ReadonlyFieldRow
          label={t("Email", { ns: "OrganisationProfile" })}
          value={profile.contactEmail}
          onEdit={openEmailEdit}
        />
      ) : (
        <EditableFieldRow
          label={t("Email", { ns: "OrganisationProfile" })}
          saving={saving}
          confirmDisabled={emailSource === "other" && !customEmail.trim()}
          onCancel={() => setEditingField(null)}
          onConfirm={saveEmail}
        >
          <RadioGroup
            value={emailSource}
            onChange={e => setEmailSource(e.target.value as SimpleSource)}
          >
            {organisation.email && (
              <FormControlLabel
                value="org"
                control={<Radio size="small" disabled={saving} />}
                label={organisation.email}
                sx={formControlOverrideSx}
              />
            )}
            <FormControlLabel
              value="other"
              control={<Radio size="small" disabled={saving} />}
              label={t("Other", { ns: "Global" })}
              sx={formControlOverrideSx}
            />
          </RadioGroup>
          {emailSource === "other" && (
            <MuiTextField
              size="small"
              label={t("Email", { ns: "OrganisationProfile" })}
              value={customEmail}
              onChange={e => setCustomEmail(e.target.value)}
              disabled={saving}
              fullWidth
            />
          )}
        </EditableFieldRow>
      )}

      {editingField !== "phones" ? (
        <ReadonlyFieldRow
          label={t("Phone", { ns: "Global" })}
          value={phonesDisplayValue}
          onEdit={openPhonesEdit}
          extraSpacing={profile.contactPhones?.length > 1 ? "3px" : undefined}
        />
      ) : (
        <EditableFieldRow
          label={t("Phone", { ns: "Global" })}
          saving={saving}
          onCancel={() => setEditingField(null)}
          onConfirm={savePhones}
          extraSpacing="7px"
        >
          <Stack pb={0.5}>
            {orgToggles.length > 0 && (
              <Stack spacing={0.5}>
                {orgToggles.map(phone => (
                  <Stack
                    key={phone.id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Switch
                      size="small"
                      checked={phone.enabled}
                      onChange={e =>
                        setOrgToggles(prev =>
                          prev.map(p =>
                            p.id === phone.id
                              ? { ...p, enabled: e.target.checked }
                              : p
                          )
                        )
                      }
                      disabled={saving}
                    />
                    <Typography
                      variant="body2"
                      color={phone.enabled ? "text.primary" : "text.disabled"}
                    >
                      {phone.number}
                      {phone.type ? ` (${phoneTypeLabels[phone.type]})` : ""}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          <Formik<PhonesFormValues>
            initialValues={customPhonesInitial}
            onSubmit={() => {}}
            enableReinitialize={false}
          >
            {({ values }) => {
              customPhonesRef.current = values;
              return (
                <PhonesField
                  name="phones"
                  buttonLabel={t("Add phone number", {
                    ns: "OrganisationProfile",
                  })}
                  value={values.phones}
                  disabled={saving}
                  autoSubmit={false}
                />
              );
            }}
          </Formik>
        </EditableFieldRow>
      )}
    </Stack>
  );
}

function ServicesSection({ organisation, profile }: Props) {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["OrganisationProfile", "Global"]);

  const profileCategories = profile.categories ?? [];
  const orgCategories = organisation.categories ?? [];

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [servicesSource, setServicesSource] = useState<SimpleSource>("org");
  const [customCategories, setCustomCategories] =
    useState<string[]>(profileCategories);

  const [modify] = useOrganisationWebsitesDetailsBox_ModifyProfileMutation({
    client,
  });

  function openEdit() {
    setServicesSource(
      orgCategories.length > 0 && isEqual(profileCategories, orgCategories)
        ? "org"
        : "other"
    );
    setCustomCategories(profileCategories);
    setIsEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await modify({
        variables: {
          input: {
            slug: profile.slug,
            categories:
              servicesSource === "org" ? orgCategories : customCategories,
          },
        },
      });
      setIsEditing(false);
    } catch (err) {
      if (err instanceof Error)
        enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={1}>
      {!isEditing ? (
        <CategoriesLabeledValue
          heading={
            <Typography pb={"6px"}>
              {t("Services displayed", { ns: "OrganisationProfile" })}
            </Typography>
          }
          categoryIds={profileCategories}
          rightButton={
            <IconButton color="primary" size="small" onClick={openEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          }
        />
      ) : (
        <EditableFieldRow
          heading={
            <Typography pb={"6px"}>
              {t("Services displayed", { ns: "OrganisationProfile" })}
            </Typography>
          }
          saving={saving}
          confirmDisabled={
            servicesSource === "other" && customCategories.length === 0
          }
          onCancel={() => setIsEditing(false)}
          onConfirm={handleSave}
        >
          <RadioGroup
            value={servicesSource}
            onChange={e => {
              const val = e.target.value as SimpleSource;
              setServicesSource(val);
              if (val === "other" && customCategories.length === 0) {
                setCustomCategories(profileCategories);
              }
            }}
          >
            {orgCategories.length > 0 && (
              <FormControlLabel
                value="org"
                control={<Radio size="small" disabled={saving} />}
                label={
                  <Stack spacing={0.5} mt={0.5}>
                    <Typography variant="body2">
                      {t("Organisation services", {
                        ns: "OrganisationProfile",
                      })}
                      :
                    </Typography>
                    <CategoriesChips categoryIds={orgCategories} />
                  </Stack>
                }
              />
            )}
            <FormControlLabel
              value="other"
              control={<Radio size="small" disabled={saving} />}
              label={t("Other", { ns: "Global" })}
              sx={formControlOverrideSx}
            />
          </RadioGroup>

          {servicesSource === "other" && (
            <CategoriesInput
              categoryIds={customCategories}
              onChange={cats => setCustomCategories(cats.map(c => c.id))}
              onClear={() => setCustomCategories([])}
              disabled={saving}
              label={""}
            />
          )}
        </EditableFieldRow>
      )}
    </Stack>
  );
}

export function OrganisationWebsitesDetailsBox({
  organisation,
  profile,
}: Props) {
  const { t } = useTranslate(["OrganisationProfile"]);

  return (
    <SectionCard
      title={t("Details for website", { ns: "OrganisationProfile" })}
    >
      <Stack spacing={1.5} divider={<Divider />}>
        <ContactSection organisation={organisation} profile={profile} />
        <ServicesSection organisation={organisation} profile={profile} />
      </Stack>
    </SectionCard>
  );
}
